import type { Vehicle } from '../vehicles/types';
import type { VehicleService } from '../vehicles/vehicleSystem';
import type { Mixer } from './mixer';
import { distanceCutoff, distanceGain, farFade, panFor, type Listener } from './spatial';

/**
 * Burning and wrecked cars crackle (docs/PERF.md "Effects"): a low roar (brown noise through a band-pass,
 * swelling slowly) under a scatter of pops (white noise through a high-pass, gated open for a few
 * milliseconds at random intervals, scheduled a little ahead of the clock). Three voices, assigned to the
 * nearest fires within ~30 m like the sirens; a wreck's dying fire sits lower than a car in full blaze.
 */
const POOL_SIZE = 3;
/** Full level to ~28 m (3.5 × REF), gone by ~48 m. */
const REF = 8;
const RANGE = REF * 6;
const RANGE_SQ = RANGE * RANGE;
const SCAN_INTERVAL = 0.25;
const RELEASE_TIME = 0.4;
const HORIZON = 0.3;

interface Nodes {
  out: GainNode;
  pan: StereoPannerNode;
  dist: BiquadFilterNode;
  roar: GainNode;
  pop: GainNode;
  all: AudioNode[];
  sources: AudioBufferSourceNode[];
}

function buildNodes(mx: Mixer): Nodes {
  const ctx = mx.ctx as AudioContext;
  const t = ctx.currentTime;
  const all: AudioNode[] = [];
  const reg = <T extends AudioNode>(n: T): T => {
    all.push(n);
    return n;
  };
  const out = reg(ctx.createGain());
  out.gain.value = 0;
  const pan = reg(ctx.createStereoPanner());
  const dist = reg(ctx.createBiquadFilter());
  dist.type = 'lowpass';
  dist.frequency.value = 12000;
  dist.Q.value = 0.5;
  const sum = reg(ctx.createGain());
  sum.gain.value = 1;

  // the roar
  const brown = mx.noiseSource('brown');
  const bp = reg(ctx.createBiquadFilter());
  bp.type = 'bandpass';
  bp.frequency.value = 330;
  bp.Q.value = 0.6;
  const roar = reg(ctx.createGain());
  roar.gain.value = 0.7;
  brown.connect(bp).connect(roar).connect(sum);
  const lfo = reg(ctx.createOscillator());
  lfo.frequency.value = 0.9;
  const lfoDepth = reg(ctx.createGain());
  lfoDepth.gain.value = 0.18;
  lfo.connect(lfoDepth).connect(roar.gain);

  // the crackle: white noise, high-passed, gated by the pops scheduled in drive()
  const white = mx.noiseSource('white');
  const hp = reg(ctx.createBiquadFilter());
  hp.type = 'highpass';
  hp.frequency.value = 2400;
  hp.Q.value = 0.7;
  const pop = reg(ctx.createGain());
  pop.gain.value = 0;
  white.connect(hp).connect(pop).connect(sum);

  sum.connect(dist).connect(pan).connect(out);
  mx.startNoise(brown, t);
  mx.startNoise(white, t);
  lfo.start(t);
  return { out, pan, dist, roar, pop, all, sources: [brown, white] };
}

type VoiceState = 'free' | 'active' | 'releasing';

class FireVoice {
  vehicle: Vehicle | null = null;
  state: VoiceState = 'free';
  level = 0;
  releaseLeft = 0;
  fresh = false;
  connected = false;
  nextPop = 0;
  nodes: Nodes | null = null;
}

/** A car is on fire while it burns before the blast and as a wreck afterwards. */
export function burning(v: Vehicle): boolean {
  return v.condition === 'burning' || v.condition === 'wrecked';
}

/** Level 0..1 of a fire `d` metres away: `big` for a car in full blaze, a wreck's dying fire lower. */
export function fireLevel(d: number, big: boolean): number {
  return distanceGain(d, REF) * farFade(d, REF) * (big ? 0.6 : 0.32);
}

export class FirePool {
  private readonly voices: FireVoice[] = [];
  private scan = 0;
  private readonly candV: (Vehicle | null)[] = new Array<Vehicle | null>(POOL_SIZE).fill(null);
  private readonly candD: number[] = new Array<number>(POOL_SIZE).fill(Infinity);

  constructor(
    private readonly mx: Mixer,
    private readonly listener: Listener,
  ) {
    for (let i = 0; i < POOL_SIZE; i++) this.voices.push(new FireVoice());
  }

  get activeCount(): number {
    let n = 0;
    for (const v of this.voices) if (v.state === 'active') n++;
    return n;
  }

  update(dt: number, vehicles: VehicleService | undefined): void {
    if (!vehicles) return;
    this.scan -= dt;
    if (this.scan <= 0) {
      this.scan = SCAN_INTERVAL;
      this.reassign(vehicles);
    }
    const live = this.mx.running && this.mx.ready;
    const now = this.mx.now();
    for (const v of this.voices) {
      if (v.state === 'free') continue;
      if (v.state === 'releasing') {
        v.releaseLeft -= dt;
        if (live && v.nodes) v.nodes.out.gain.setTargetAtTime(0, now, 0.1);
        if (v.releaseLeft <= 0) this.free(v);
        continue;
      }
      const veh = v.vehicle;
      if (!veh || vehicles.get(veh.id) !== veh || !burning(veh)) {
        this.release(v);
        continue;
      }
      try {
        this.drive(v, veh, live, now);
      } catch (e) {
        this.release(v);
        if (typeof console !== 'undefined') console.warn('[audio] fire voice failed', e);
      }
    }
  }

  private drive(v: FireVoice, veh: Vehicle, live: boolean, now: number): void {
    const l = this.listener;
    const dx = veh.pos.x - l.x;
    const dz = veh.pos.z - l.z;
    const d = Math.sqrt(dx * dx + dz * dz);
    const big = veh.condition === 'burning';
    v.level = fireLevel(d, big);
    if (!live) return;
    const n = v.nodes ?? (v.nodes = buildNodes(this.mx));
    if (!v.connected) {
      n.out.connect(this.mx.buses.sfx);
      v.connected = true;
    }
    if (v.fresh) {
      n.out.gain.cancelScheduledValues(now);
      n.out.gain.setValueAtTime(0, now);
      n.pop.gain.cancelScheduledValues(now);
      n.pop.gain.setValueAtTime(0, now);
      v.nextPop = now + 0.02;
      v.fresh = false;
    }
    // Pops a little ahead of the clock: denser and louder in a full blaze.
    const g = n.pop.gain;
    while (v.nextPop < now + HORIZON) {
      const at = Math.max(v.nextPop, now);
      const a = (big ? 0.5 : 0.3) * (0.5 + Math.random() * 0.5);
      g.setValueAtTime(0, at);
      g.linearRampToValueAtTime(a, at + 0.003);
      g.setTargetAtTime(0, at + 0.003, 0.006 + Math.random() * 0.012);
      v.nextPop = at + (big ? 0.03 : 0.06) + Math.random() * (big ? 0.11 : 0.22);
    }
    n.roar.gain.setTargetAtTime(big ? 0.7 : 0.4, now, 0.3);
    n.dist.frequency.setTargetAtTime(Math.min(12000, distanceCutoff(d)), now, 0.1);
    n.pan.pan.setTargetAtTime(panFor(dx, dz, l.rx, l.rz), now, 0.08);
    n.out.gain.setTargetAtTime(v.level, now, 0.12);
  }

  private reassign(vehicles: VehicleService): void {
    const l = this.listener;
    const cv = this.candV;
    const cd = this.candD;
    for (let i = 0; i < POOL_SIZE; i++) {
      cv[i] = null;
      cd[i] = Infinity;
    }
    for (const veh of vehicles.all()) {
      if (!burning(veh)) continue;
      const dx = veh.pos.x - l.x;
      const dz = veh.pos.z - l.z;
      const d2 = dx * dx + dz * dz;
      if (d2 > RANGE_SQ || d2 >= (cd[POOL_SIZE - 1] as number)) continue;
      let i = POOL_SIZE - 1;
      while (i > 0 && d2 < (cd[i - 1] as number)) {
        cd[i] = cd[i - 1] as number;
        cv[i] = cv[i - 1] as Vehicle | null;
        i--;
      }
      cd[i] = d2;
      cv[i] = veh;
    }
    for (const v of this.voices) {
      if (v.state !== 'active') continue;
      if (!cv.includes(v.vehicle)) this.release(v);
    }
    for (let i = 0; i < POOL_SIZE; i++) {
      const veh = cv[i];
      if (!veh) continue;
      if (this.voices.some((v) => v.state === 'active' && v.vehicle === veh)) continue;
      const free = this.voices.find((v) => v.state === 'free');
      if (!free) break;
      free.vehicle = veh;
      free.state = 'active';
      free.fresh = true;
      free.level = 0;
    }
  }

  private release(v: FireVoice): void {
    v.state = 'releasing';
    v.releaseLeft = RELEASE_TIME;
  }

  private free(v: FireVoice): void {
    if (v.nodes && v.connected) {
      v.nodes.out.disconnect();
      v.connected = false;
    }
    v.state = 'free';
    v.vehicle = null;
  }

  dispose(): void {
    for (const v of this.voices) {
      const n = v.nodes;
      if (!n) continue;
      for (const s of n.sources) {
        try {
          s.stop();
        } catch {
          /* not started */
        }
      }
      for (const node of n.all) node.disconnect();
      v.nodes = null;
    }
  }
}
